import './i18n'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { lazy, Suspense } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'

import App from './App'
import { BrandLoading } from './components/common'
import ErrorBoundary from './hoc/ErrorBoundary'
import { router } from './router'
import { initAdjust, preloadAdjustWebUUID } from './utils/adjust'
import { preloadDeviceId } from './utils/device-id'
import { captureFirstLandingUrl } from './utils/first-landing-url'
import { onIdle } from './utils/on-idle'

// GTM is bootstrapped by the inline <script> in index.html (gated on the
// canonical host). Don't also call TagManager.initialize here — it would
// inject the same gtm.js loader and double every pageview signal.

dayjs.extend(customParseFormat)

// Record the first landing URL before React renders and before the router can
// redirect, so we keep the genuine entry URL with its original query params.
captureFirstLandingUrl()

// Warm up the device fingerprint so it is ready by the time the user submits
// the register form.
preloadDeviceId()

// Initialise the Adjust web SDK and warm up its web_uuid once the browser is
// idle. The SDK is now dynamically imported (see utils/adjust), so deferring
// the call keeps its ~200KB download + init off the critical boot path. Init
// must run before the warm-up, so they share one idle callback. Both no-op
// outside the production build and inside the native wrapper.
onIdle(() => {
  initAdjust()
  preloadAdjustWebUUID()
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60_000,
      refetchOnWindowFocus: false,
    },
  },
})

// Dev-only: lazily import the devtools so neither it nor its `superjson`
// dependency are bundled into the production app. In prod `import.meta.env.DEV`
// is statically false, so the dynamic import() is never referenced and rollup
// drops the chunk entirely.
const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-query-devtools').then(m => ({
        default: m.ReactQueryDevtools,
      })),
    )
  : null

const Root = () => (
  <QueryClientProvider client={queryClient}>
    <ErrorBoundary>
      <Suspense fallback={<BrandLoading fullScreen />}>
        <App />
      </Suspense>
    </ErrorBoundary>
    {ReactQueryDevtools && (
      <Suspense fallback={null}>
        <ReactQueryDevtools initialIsOpen={false} />
      </Suspense>
    )}
  </QueryClientProvider>
)

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const rootElement = document.getElementById('root')!
// react-snap pre-renders into #root: hydrate when content is present, otherwise
// mount fresh. (The previous `if (!innerHTML)` guard made hydrateRoot
// unreachable — pre-rendered pages never mounted.)
const renderApp = () => {
  if (rootElement.hasChildNodes()) {
    hydrateRoot(rootElement, <Root />)
  } else {
    createRoot(rootElement).render(<Root />)
  }
}

// Resolve the initial route matches BEFORE the first render. The router's
// <Matches> picks its wrapper element by `state.matches.length` (Suspense while
// empty, a plain fragment once filled); if the first render happens with no
// matches, the next parent re-render after they fill swaps the wrapper type,
// and React unmounts and remounts the entire route tree — re-firing every
// bootstrap effect (duplicate settings/components/etc. requests) and flashing
// the splash screen. With matches pre-loaded the wrapper never changes. First
// paint is not delayed: the route tree could not commit before this load
// finished anyway (the index.html splash covers the wait).
//
// load() also awaits the matched route's lazy component chunk, so a chunk
// request that stalls without ever settling would pin the splash up forever.
// Racing a timeout guarantees the app still mounts: a timed-out mount merely
// degrades to the pre-load behavior (suspend inside the tree, worst case a
// route-tree remount), which beats an unrecoverable splash. renderApp runs
// exactly once — it is attached only to the race, and a race settles once.
const loadTimeout = new Promise<void>(resolve => {
  setTimeout(resolve, 10_000)
})
Promise.race([router.load().catch(console.error), loadTimeout]).then(renderApp)
